import { AdUnitEnv } from '@pubstack/common/src/pbs/adunit'
import { UpgradeOffer } from '@pubstack/common/src/upgrade'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'

export type AdUnitTabPath = 'web' | 'server-side' | 'inapp' | 'amp'

export type AdUnitTab = {
  path: AdUnitTabPath
  label: string
  /** The offer the tab belongs to. Missing offer ⇒ upgrade pitch instead of the list. */
  offer: UpgradeOffer
  /**
   * The PBS environment this tab lists. Absent on Web, which is One's own inventory and has no `env`
   * at all — the two vocabularies meet here and nowhere else. Note `AdUnitEnv` also has a `'web'`
   * value, which nothing writes and which does NOT mean this tab.
   */
  env?: Exclude<AdUnitEnv, 'web'>
  trackingLabel: TrackingLabel
}

export const AD_UNIT_TABS: AdUnitTab[] = [
  { path: 'web', label: 'Web', offer: 'one', trackingLabel: 'adm-ad-units-tab-apply' },
  { path: 'server-side', label: 'Server-side', offer: 'node', env: 's2s', trackingLabel: 'pbs-ad-units-tab-apply' },
  { path: 'inapp', label: 'In-app', offer: 'node', env: 'inapp', trackingLabel: 'pbs-inapp-ad-units-tab-apply' },
  { path: 'amp', label: 'AMP', offer: 'node', env: 'amp', trackingLabel: 'pbs-amp-ad-units-tab-apply' },
]

/** What the person looking can use: One for the scope, Node for the viewer (access, or staff on a staff-only scope). */
export type AdUnitProducts = { hasOne: boolean; hasNode: boolean }

/**
 * A tab shows its table when the viewer has the product behind it, and the upgrade pitch otherwise.
 *
 * Node is the viewer's, not the scope's (`useNodeStaffAccess().hasNode`): on a scope onboarded but not yet opened
 * to its users, Pubstack admins have the Node tabs and everybody else sees them as on a scope that never bought it.
 */
export const isTabAvailable = (tab: AdUnitTab, { hasOne, hasNode }: AdUnitProducts): boolean => (tab.offer === 'one' ? hasOne : hasNode)

/** The tab to land on: the first the viewer can actually use, else Web, which pitches One. */
export const defaultAdUnitTab = (products: AdUnitProducts): AdUnitTabPath => (AD_UNIT_TABS.find((tab) => isTabAvailable(tab, products)) ?? AD_UNIT_TABS[0]).path
